import { useState, useDeferredValue, useEffect, useRef } from 'react';
import { useTasksStore } from '../../stores/tasks';
import { useAuthStore } from '../../stores/auth';
import { useChatStore } from '../../stores/chat';
import type { Task, AppUser } from '../../lib/api';
import { apiErrorMessage } from '../../lib/apiError';
import { isToday, isPast, parseISO, format } from 'date-fns';
import Sheet from '../ui/Sheet';
import SegmentedControl from '../ui/SegmentedControl';
import { Check, ChevronDown } from '../../lib/icons';
import { TaskDetailBody } from './TaskDetailBody';
import { groupTasks, groupStatus, statusNext, statusLabel, type StatusValue, type TaskGroup } from './taskGroups';

function DueBadge({ due_at, done }: { due_at: string | null; done: boolean }) {
  if (!due_at) return null;
  const date = parseISO(due_at);
  const label = format(date, 'd MMM yyyy');
  if (done)          return <Badge label={label} bg="rgba(29,158,117,.1)"   color="var(--teal-700)" />;
  if (isToday(date)) return <Badge label="Today"  bg="var(--amber-50)"       color="#92400E" />;
  if (isPast(date))  return <Badge label="Overdue" bg="var(--red-50)"        color="var(--red)" />;
  return <Badge label={label} bg="var(--slate-100)" color="var(--xs)" />;
}

function Badge({ label, bg, color }: { label: string; bg: string; color: string }) {
  return (
    <span style={{ fontSize: '0.67rem', padding: '2px 7px', borderRadius: 3, background: bg, color, flexShrink: 0 }}>
      {label}
    </span>
  );
}

function StatusDot({ status, canEdit, onClick }: { status: StatusValue; canEdit: boolean; onClick: () => void }) {
  const base: React.CSSProperties = { width: 13, height: 13, borderRadius: '50%', flexShrink: 0, transition: 'box-shadow .15s' };
  const styles: Record<StatusValue, React.CSSProperties> = {
    open:        { ...base, border: '2px solid var(--vb2)', background: 'transparent' },
    in_progress: { ...base, background: '#3B82F6', boxShadow: '0 0 6px rgba(59,130,246,.4)' },
    done:        { ...base, background: 'var(--green)', boxShadow: '0 0 6px rgba(29,158,117,.4)' },
  };
  return (
    <div
      style={{ ...styles[status], cursor: canEdit ? 'pointer' : 'default', opacity: canEdit ? 1 : 0.45 }}
      title={canEdit ? `${statusLabel(status)} — click to advance` : 'Only the assignee can change status'}
      onClick={canEdit ? onClick : undefined}
    />
  );
}

// ── Add Task Modal ─────────────────────────────────────────────────────────

const AVATAR_COLORS = ['#0F6E56', '#059669', '#D97706', '#DC2626', '#1D4ED8', '#7C3AED'];

function UserChip({ user, onRemove }: { user: AppUser; onRemove: () => void }) {
  const initials = user.name.split(' ').map((p) => p[0]).join('').toUpperCase().slice(0, 2);
  const color = AVATAR_COLORS[user.name.charCodeAt(0) % AVATAR_COLORS.length];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      background: 'var(--teal-50)', border: '1px solid var(--v1)',
      borderRadius: 4, padding: '2px 6px 2px 4px', fontSize: '0.72rem', color: 'var(--v2)',
    }}>
      <span style={{
        width: 16, height: 16, borderRadius: 2, background: color,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '0.55rem', fontWeight: 700, color: '#fff', flexShrink: 0,
      }}>{initials}</span>
      {user.name}
      <span
        onClick={onRemove}
        style={{ cursor: 'pointer', color: 'var(--xs)', lineHeight: 1, marginLeft: 1, fontSize: '0.75rem' }}
      >✕</span>
    </span>
  );
}

function AssigneePicker({
  users, selected, onChange, currentUserId,
}: {
  users: AppUser[];
  selected: string[];
  onChange: (ids: string[]) => void;
  currentUserId: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function toggle(id: string) {
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  }

  const filtered = query
    ? users.filter((u) => u.name.toLowerCase().includes(query.toLowerCase()))
    : users;

  const selectedUsers = users.filter((u) => selected.includes(u.id));

  const inputStyle: React.CSSProperties = {
    background: 'var(--vs2)', border: '1px solid var(--vb)', borderRadius: 4,
    color: 'var(--text)', fontSize: '0.8rem', padding: '6px 10px', width: '100%',
    outline: 'none', boxSizing: 'border-box',
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {/* Selected chips */}
      {selectedUsers.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 6 }}>
          {selectedUsers.map((u) => (
            <UserChip key={u.id} user={u} onRemove={() => toggle(u.id)} />
          ))}
        </div>
      )}
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          ...inputStyle, textAlign: 'left', cursor: 'pointer',
          color: selectedUsers.length ? 'var(--text)' : 'var(--xs)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}
      >
        <span>{selectedUsers.length === 0 ? '— Unassigned —' : `${selectedUsers.length} assignee${selectedUsers.length > 1 ? 's' : ''} selected`}</span>
        <ChevronDown size={13} strokeWidth={1.8} style={{ color: 'var(--xs)', transform: open ? 'rotate(180deg)' : undefined, transition: 'transform .12s' }} />
      </button>

      {/* Dropdown */}
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
          background: 'var(--vs1)', border: '1px solid var(--vb)', borderRadius: 6,
          boxShadow: '0 4px 16px rgba(0,0,0,.18)', zIndex: 10, overflow: 'hidden',
        }}>
          <div style={{ padding: '8px 8px 4px' }}>
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search members…"
              style={{ ...inputStyle, fontSize: '0.75rem', padding: '5px 8px' }}
            />
          </div>
          <div style={{ maxHeight: 180, overflowY: 'auto' }}>
            {filtered.length === 0 ? (
              <div style={{ padding: '8px 12px', fontSize: '0.75rem', color: 'var(--xs)' }}>No results</div>
            ) : filtered.map((u) => {
              const initials = u.name.split(' ').map((p) => p[0]).join('').toUpperCase().slice(0, 2);
              const color = AVATAR_COLORS[u.name.charCodeAt(0) % AVATAR_COLORS.length];
              const checked = selected.includes(u.id);
              return (
                <div
                  key={u.id}
                  onClick={() => toggle(u.id)}
                  style={{
                    padding: '7px 12px', display: 'flex', alignItems: 'center', gap: 9,
                    cursor: 'pointer', background: checked ? 'rgba(29,158,117,.08)' : 'transparent',
                    transition: 'background .1s',
                  }}
                  onMouseEnter={(e) => { if (!checked) (e.currentTarget as HTMLElement).style.background = 'var(--hover)'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = checked ? 'rgba(29,158,117,.08)' : 'transparent'; }}
                >
                  <div style={{
                    width: 14, height: 14, borderRadius: 3, flexShrink: 0,
                    border: `2px solid ${checked ? 'var(--v1)' : 'var(--vb2)'}`,
                    background: checked ? 'var(--v1)' : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all .1s',
                  }}>
                    {checked && <Check size={10} strokeWidth={2.5} style={{ color: 'var(--text-on-accent)' }} />}
                  </div>
                  <div style={{
                    width: 22, height: 22, borderRadius: 3, background: color, flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.6rem', fontWeight: 700, color: '#fff',
                  }}>{initials}</div>
                  <span style={{ fontSize: '0.8rem', color: 'var(--text)', flex: 1 }}>
                    {u.name}{u.id === currentUserId ? <span style={{ color: 'var(--xs)', fontSize: '0.7rem' }}> (me)</span> : null}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export function AddTaskModal({ onClose, threadId }: { onClose: () => void; threadId?: string }) {
  const createTasks = useTasksStore((s) => s.createTasks);
  const currentUser = useAuthStore((s) => s.user);
  const users = useChatStore((s) => s.users); // already loaded by AppPage — no separate fetch needed
  const [title, setTitle]           = useState('');
  const [assigneeIds, setAssigneeIds] = useState<string[]>(currentUser?.id ? [currentUser.id] : []);
  const [description, setDescription] = useState('');
  const [saving, setSaving]         = useState(false);
  const [err, setErr]               = useState('');

  async function submit() {
    if (!title.trim()) { setErr('Title is required'); return; }
    setSaving(true);
    try {
      await createTasks({
        title: title.trim(),
        assignedTo: assigneeIds,
        threadId,
        description: description.trim() || undefined,
      });
      onClose();
    } catch (e: unknown) {
      setErr(apiErrorMessage(e, 'Failed to create task'));
      setSaving(false);
    }
  }

  const inputStyle: React.CSSProperties = {
    background: 'var(--vs2)', border: '1px solid var(--vb)', borderRadius: 4,
    color: 'var(--text)', fontSize: '0.8rem', padding: '6px 10px', width: '100%',
    outline: 'none', boxSizing: 'border-box',
  };
  const labelStyle: React.CSSProperties = {
    fontSize: '0.65rem', color: 'var(--xs)', fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: '0.07em', display: 'block', marginBottom: 4,
  };

  return (
    <Sheet title="New Task" onClose={onClose}>
      <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Title */}
        <div>
          <label style={labelStyle}>Title <span style={{ color: 'var(--red)' }}>*</span></label>
          <input
            value={title}
            onChange={(e) => { setTitle(e.target.value); setErr(''); }}
            placeholder="What needs to be done?"
            style={inputStyle}
            autoFocus
            onKeyDown={(e) => { if (e.key === 'Enter') submit(); if (e.key === 'Escape') onClose(); }}
          />
        </div>

        {/* Assignees */}
        <div>
          <label style={labelStyle}>
            Assignees
            {assigneeIds.length > 1 && (
              <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, marginLeft: 6, color: 'var(--v2)' }}>
                — creates {assigneeIds.length} tasks
              </span>
            )}
          </label>
          <AssigneePicker
            users={users}
            selected={assigneeIds}
            onChange={setAssigneeIds}
            currentUserId={currentUser?.id ?? ''}
          />
        </div>

        {/* Details */}
        <div>
          <label style={labelStyle}>Details</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Add any extra context or notes…"
            rows={3}
            style={{
              ...inputStyle, resize: 'vertical', minHeight: 70,
              fontFamily: 'inherit', lineHeight: 1.5,
            }}
          />
        </div>

        {err && <div style={{ fontSize: '0.75rem', color: 'var(--red)', marginTop: -8 }}>{err}</div>}

        {/* Actions */}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{
            background: 'transparent', border: '1px solid var(--vb)', borderRadius: 4,
            color: 'var(--muted)', fontSize: '0.75rem', padding: '6px 16px', cursor: 'pointer',
          }}>
            Cancel
          </button>
          <button onClick={submit} disabled={saving} style={{
            background: saving ? 'var(--vb2)' : 'var(--v1)', border: 'none', borderRadius: 4,
            color: saving ? 'var(--muted)' : 'var(--text-on-accent)', fontSize: '0.75rem', fontWeight: 700,
            padding: '6px 18px', cursor: saving ? 'wait' : 'pointer',
          }}>
            {saving ? 'Creating…' : assigneeIds.length > 1 ? `Create ${assigneeIds.length} Tasks` : 'Create Task'}
          </button>
        </div>
      </div>
    </Sheet>
  );
}

// ── Stacked assignee avatars ──────────────────────────────────────────────────

const AVATAR_COLORS_CARD = ['#0F6E56', '#059669', '#D97706', '#DC2626', '#1D4ED8', '#7C3AED'];

function avatarColor(name: string | null) {
  return AVATAR_COLORS_CARD[(name?.charCodeAt(0) ?? 0) % AVATAR_COLORS_CARD.length];
}
function avatarInitials(task: Task) {
  return task.assignee_initials
    ?? task.assignee_name?.split(' ').map((p) => p[0]).join('').toUpperCase().slice(0, 2)
    ?? '?';
}

function AssigneeStack({ tasks }: { tasks: Task[] }) {
  const MAX_SHOWN = 3;
  const shown = tasks.slice(0, MAX_SHOWN);
  const overflow = tasks.length - MAX_SHOWN;
  const names = tasks.map((t) => t.assignee_name ?? 'Unknown').join(', ');
  return (
    <div title={names} style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
      {shown.map((t, i) => (
        <div key={t.id} style={{
          width: 24, height: 24, borderRadius: 3, flexShrink: 0,
          background: avatarColor(t.assignee_name ?? null),
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '0.6rem', fontWeight: 700, color: '#fff',
          marginLeft: i === 0 ? 0 : -6,
          border: '2px solid var(--vs2)',
          zIndex: shown.length - i,
          position: 'relative',
        }}>
          {avatarInitials(t)}
        </div>
      ))}
      {overflow > 0 && (
        <div style={{
          width: 24, height: 24, borderRadius: 3, flexShrink: 0,
          background: 'var(--vb2)', marginLeft: -6, border: '2px solid var(--vs2)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '0.58rem', fontWeight: 700, color: 'var(--xs)', position: 'relative',
        }}>
          +{overflow}
        </div>
      )}
    </div>
  );
}

// ── Task card ──────────────────────────────────────────────────────────────

function TaskCard({ group, currentUserId, selected, onSelected }: { group: TaskGroup; currentUserId: string; selected?: boolean; onSelected?: () => void }) {
  const updateTask  = useTasksStore((s) => s.updateTask);
  const [expanded, setExpanded] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (selected) {
       
      setExpanded(true);
      setTimeout(() => {
        cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        onSelected?.();
      }, 50);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  // My task within this group (if I'm an assignee)
  const myTask = group.tasks.find((t) => t.assigned_to === currentUserId);
  const repr   = group.representative;

  const effectiveStatus: StatusValue = groupStatus(group, currentUserId);

  const canEdit = !!myTask;
  const isDone  = effectiveStatus === 'done';
  const contextLabel = repr.client_account_name ?? repr.thread_name;

  return (
    <div ref={cardRef} style={{
      background: 'var(--vs2)', border: `1px solid ${selected ? 'var(--v2)' : expanded ? 'var(--v1)' : 'var(--vb)'}`,
      borderRadius: 4, marginBottom: 5,
      transition: 'border-color .12s', boxShadow: selected ? '0 0 0 2px rgba(29,158,117,.25)' : '0 1px 3px rgba(13,27,42,.05)',
    }}>
      {/* Main row */}
      <div
        style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 12 }}
        onMouseEnter={(e) => { if (!expanded) (e.currentTarget as HTMLElement).style.background = 'rgba(13,27,42,.02)'; }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
      >
        <StatusDot
          status={effectiveStatus}
          canEdit={canEdit}
          onClick={() => myTask && updateTask(myTask.id, { status: statusNext(myTask.status) })}
        />

        {/* Title + context */}
        <div
          style={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
          onClick={() => setExpanded((v) => !v)}
        >
          <div style={{
            fontSize: '0.8rem', fontWeight: 600,
            color: isDone ? 'var(--xs)' : 'var(--text)',
            textDecoration: isDone ? 'line-through' : 'none',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {repr.title}
          </div>
          <div style={{ display: 'flex', gap: 6, marginTop: 3, alignItems: 'center', flexWrap: 'wrap' }}>
            {contextLabel && (
              <span style={{
                fontSize: '0.65rem',
                color: isDone ? 'var(--teal-700)' : 'var(--v1)',
                background: isDone ? 'rgba(29,158,117,.08)' : 'var(--teal-50)',
                padding: '1px 6px', borderRadius: 3,
              }}>
                {contextLabel}
              </span>
            )}
            <span style={{ fontSize: '0.65rem', color: 'var(--xs)' }}>
              {format(parseISO(repr.created_at), 'd MMM yyyy')}
            </span>
          </div>
        </div>

        {/* Stacked assignee avatars */}
        <AssigneeStack tasks={group.tasks} />

        <DueBadge due_at={repr.due_at} done={isDone} />

        {/* Expand chevron */}
        <div
          onClick={() => setExpanded((v) => !v)}
          style={{ color: 'var(--xs)', fontSize: '0.7rem', cursor: 'pointer', flexShrink: 0, padding: '0 2px', userSelect: 'none' }}
        >
          {expanded ? '▲' : '▼'}
        </div>
      </div>

      {/* Inline edit drawer — shows the current user's task if they're an assignee */}
      {expanded && <TaskDetailBody group={group} currentUserId={currentUserId} onClose={() => setExpanded(false)} />}
    </div>
  );
}

// ── Section header ─────────────────────────────────────────────────────────

function SectionLabel({ label, count, color }: { label: string; count: number; color: string }) {
  if (count === 0) return null;
  return (
    <div style={{
      fontSize: '0.63rem', fontWeight: 700, textTransform: 'uppercase',
      letterSpacing: '0.1em', color: 'var(--shell-xs)', margin: '18px 0 8px',
      display: 'flex', alignItems: 'center', gap: 8,
    }}>
      <span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: color, flexShrink: 0 }} />
      {label}
      <span style={{ background: 'var(--hover-strong)', borderRadius: 3, padding: '0 5px', color: 'var(--shell-xs)' }}>{count}</span>
      <span style={{ flex: 1, height: 1, background: 'var(--border-subtle)' }} />
    </div>
  );
}

// ── Main panel ─────────────────────────────────────────────────────────────

export default function TasksPanel({ selectedTaskId, onTaskViewed }: { selectedTaskId?: string | null; onTaskViewed?: () => void }) {
  const tasks      = useTasksStore((s) => s.tasks);
  const filter     = useTasksStore((s) => s.filter);
  const setFilter  = useTasksStore((s) => s.setFilter);
  const loading    = useTasksStore((s) => s.loading);
  const currentUser = useAuthStore((s) => s.user);

  const [search, setSearch]       = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const deferredSearch = useDeferredValue(search.toLowerCase().trim());

  const byFilter = filter === 'mine'
    ? tasks.filter((t) => t.assigned_to === currentUser?.id)
    : tasks;

  const visibleTasks = deferredSearch
    ? byFilter.filter((t) => t.title.toLowerCase().includes(deferredSearch))
    : byFilter;

  // Collapse multi-assignee tasks into single rows
  const groups = groupTasks(visibleTasks);

  const open       = groups.filter((g) => groupStatus(g, currentUser?.id) === 'open');
  const inProgress = groups.filter((g) => groupStatus(g, currentUser?.id) === 'in_progress');
  const done       = groups.filter((g) => groupStatus(g, currentUser?.id) === 'done');

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {showAddModal && <AddTaskModal onClose={() => setShowAddModal(false)} />}
      {/* Header */}
      <div style={{
        padding: '10px 24px', borderBottom: '1px solid var(--shell-border)',
        background: 'var(--vs1)', display: 'flex', alignItems: 'center',
        gap: 12, flexShrink: 0,
      }}>
        <div style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--shell-text)', flexShrink: 0 }}>Tasks</div>
        {/* Search bar */}
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search tasks…"
          style={{
            flex: 1, background: 'var(--vs2)', border: '1px solid var(--vb)',
            borderRadius: 4, padding: '5px 10px', color: 'var(--text)',
            fontSize: '0.78rem', outline: 'none', minWidth: 0,
          }}
        />
        {/* Filter toggle */}
        <div style={{ flexShrink: 0 }}>
          <SegmentedControl
            size="sm"
            value={filter}
            onChange={setFilter}
            options={[
              { value: 'mine', label: 'My Tasks' },
              { value: 'all', label: 'All Tasks' },
            ]}
          />
        </div>
      </div>

      {/* Board */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '8px 24px 24px', background: 'var(--surface-app)' }}>
        {loading ? (
          <div style={{ color: 'var(--shell-xs)', textAlign: 'center', paddingTop: 48, fontSize: '0.82rem' }}>Loading…</div>
        ) : groups.length === 0 ? (
          <div style={{ color: 'var(--shell-xs)', textAlign: 'center', paddingTop: 48, fontSize: '0.82rem' }}>
            No tasks yet
          </div>
        ) : (
          <>
            <SectionLabel label="Open" count={open.length} color="var(--muted)" />
            {open.map((g) => (
              <TaskCard key={g.key} group={g} currentUserId={currentUser?.id ?? ''}
                selected={g.tasks.some((t) => t.id === selectedTaskId)} onSelected={onTaskViewed} />
            ))}

            <SectionLabel label="In Progress" count={inProgress.length} color="#3B82F6" />
            {inProgress.map((g) => (
              <TaskCard key={g.key} group={g} currentUserId={currentUser?.id ?? ''}
                selected={g.tasks.some((t) => t.id === selectedTaskId)} onSelected={onTaskViewed} />
            ))}

            <SectionLabel label="Done" count={done.length} color="var(--green)" />
            {done.map((g) => (
              <TaskCard key={g.key} group={g} currentUserId={currentUser?.id ?? ''}
                selected={g.tasks.some((t) => t.id === selectedTaskId)} onSelected={onTaskViewed} />
            ))}
          </>
        )}
      </div>

      {/* Add Task button */}
      <div style={{
        padding: '10px 24px', borderTop: '1px solid var(--shell-border)',
        background: 'var(--vs1)', flexShrink: 0,
      }}>
        <button
          onClick={() => setShowAddModal(true)}
          style={{
            width: '100%', padding: '8px 0', borderRadius: 5,
            background: 'var(--v1)', border: 'none', color: 'var(--text-on-accent)',
            fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
            transition: 'opacity .12s',
          }}
          onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.opacity = '0.88')}
          onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.opacity = '1')}
        >
          <span style={{ fontSize: '1rem', lineHeight: 1 }}>+</span> Add Task
        </button>
      </div>
    </div>
  );
}
